import React, { useState } from 'react';
import { GridColDef, GridCellParams } from '@material-ui/x-grid';
import { PeriodFormatter } from 'components/stats/utils';
import { Paper, Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { camelCaser } from 'components/utils/kpiLabelExtractor';
import { formatCategory } from 'components/athletesessions/utils';
import categoriesExtendedCell from 'components/CategoriesExtendedCell';
import tagsExtendedCell from 'components/TagsExtendedCell';
import { SeriesTrendState } from './SeriesInputs2';
import Table from '../Table';

type PropsIn = {
  tableRows: SeriesTrendState[];
  statsType: 'trend' | 'summary' | 'radar';
};

export default function SeriesTable(props: PropsIn) {
  const { tableRows, statsType } = props;
  const { t } = useTranslation();

  const isTrend = statsType === 'trend';
  const isSummary = statsType === 'summary';
  const isRadar = statsType === 'radar';
  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      headerClassName: 'table-header__subtitle',
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      width: 75,
      hideSortIcons: true,
      cellClassName: 'font-bold',
      renderCell: (params:GridCellParams) => params.row.number,
    },
    {
      field: 'period',
      headerName: t('stats.drawer.period', 'period'),
      headerClassName: 'table-header__subtitle',
      headerAlign: 'left',
      align: 'left',
      minWidth: 150,
      sortable: false,
      hideSortIcons: true,
      renderCell: (params:GridCellParams) => <span className="nolink-col-left">{params.row.period}</span>,
    },
    {
      field: 'grouping',
      headerName: t('stats.grouping', 'grouping'),
      headerClassName: 'table-header__subtitle',
      headerAlign: 'left',
      align: 'left',
      width: 130,
      sortable: false,
      hideSortIcons: true,
      hide: !isTrend,
      renderCell: (params:GridCellParams) => <span className="nolink-col-left">{params.row.grouping}</span>,
    },
    {
      field: 'athlete',
      headerName: t('table.athlete', 'athlete'),
      headerClassName: 'table-header__subtitle',
      headerAlign: 'left',
      align: 'left',
      width: 75,
      flex: 1,
      sortable: false,
      hideSortIcons: true,
      hide: isSummary,
      renderCell: (params:GridCellParams) => <span className="nolink-col-left">{params.row.athlete}</span>,
    },
    {
      field: 'KPI',
      headerName: t('stats.kpi', 'kpi').toUpperCase(),
      headerClassName: 'table-header__subtitle',
      headerAlign: 'left',
      align: 'left',
      flex:1,
      sortable: false,
      hideSortIcons: true,
      hide: isRadar,
      renderCell: (params:GridCellParams) => <span className="nolink-col-left">{params.row.KPI}</span>,
    },
    {
      field: 'categories',
      headerName: t('stats.drawer.categories', 'categories'),
      headerClassName: 'table-header__subtitle',
      headerAlign: 'left',
      align: 'left',
      width: 230,
      sortable: false,
      hideSortIcons: true,
      renderCell: (params: GridCellParams) => categoriesExtendedCell(params, 230),
    },
    {
      field: 'tags',
      headerName: t('stats.drawer.tags', 'tags'),
      headerClassName: 'table-header__subtitle',
      headerAlign: 'left',
      align: 'left',
      minWidth: 100,
      flex: 1,
      sortable: false,
      hideSortIcons: true,
      renderCell: (params: GridCellParams) => tagsExtendedCell(params, 480),
    },
  ];

  const rows = tableRows?.map((e, i) => ({
    id: i,
    number: i + 1,
    period: PeriodFormatter(e.period),
    grouping: e.groupingBy || '-',
    athlete: e.athletes?.map((a) => a.value).join(', ') || '-',
    KPI: e.KPI?.map((k) => k.value) || '-',
    categories: e.categories?.map((c) => `${formatCategory(t(`session.category.${camelCaser(c.value.toLowerCase())}`, `${c.value}`), c.color)}-`)
    || '-',
    tags: e.tags?.map((t) => t.value).join(', ') || '-',
  })) || [];

  return (
    <Paper>
      <Box p={2}>
        <Table disableCursor columns={columns} rows={rows} disableColumnFilter />
      </Box>
    </Paper>
  );
}
